import { inject, Injectable, signal } from '@angular/core';
import { ErrorHandler } from '@core/handler/error.handler';
import {
  IamAPIOrganization,
  IamAttributes,
  IamOrganization,
  Invitation,
  Manager,
  UserAttributes,
} from '@core/iam/iam.models';
import { TranslateService } from '@ngx-translate/core';
import { PCIAMErrorCodes } from '@shared/errors/error.codes';
import { CRError } from '@shared/models/error.models';
import {
  catchError,
  concat,
  EMPTY,
  endWith,
  ignoreElements,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { IamApiService } from './iam.api';

@Injectable({ providedIn: 'root' })
export class IamStore {
  private readonly api = inject(IamApiService);
  private readonly authService = inject(AuthService);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly translateService = inject(TranslateService);

  public readonly isInitialized = signal(false);

  public readonly iamOrganizations = signal<IamOrganization[]>([]);
  public readonly managers = signal<Manager[]>([]);
  public readonly invitations = signal<Invitation[]>([]);

  public initialize() {
    if (this.isInitialized()) {
      return EMPTY as Observable<void>;
    }

    return concat(this.createCROrganizations$(), this.createManagers$()).pipe(
      tap({ complete: () => this.isInitialized.set(true) }),
      catchError((error) => {
        console.error('Error initializing IAM store:', error);
        return throwError(() => error);
      }),
    );
  }

  // Reloads from the API, for example after an organization switch.
  public reinitialize() {
    return concat(this.createCROrganizations$(), this.createManagers$()).pipe(
      catchError((error) => {
        console.error('Error initializing IAM store:', error);
        return throwError(() => error);
      }),
    );
  }

  private createCROrganizations$() {
    return this.api.getIamOrganisations().pipe(
      tap((organizations) => {
        if (!organizations || organizations.length < 1) {
          this.iamOrganizations.set([]);
          this.handleCreateOrganizationsError();
        }

        const parsedOrgs = organizations.map((org) => this.parseOrganizationResponse(org));
        this.iamOrganizations.set(parsedOrgs);
      }),
      catchError(() => {
        this.iamOrganizations.set([]);
        this.handleCreateOrganizationsError();
        return EMPTY;
      }),
      ignoreElements(),
    );
  }
  private parseOrganizationResponse(org: IamAPIOrganization): IamOrganization {
    return {
      organizationId: org.organization_id,
      organizationName: org.organization_name,
      userRole: org.user_role,
    };
  }

  private createManagers$() {
    return this.api.getManagers().pipe(
      tap((res) => {
        if (res.users && res.users.length > 0) {
          this.managers.set(res.users);
        }

        if (res.invitations && res.invitations.length > 0) {
          this.invitations.set(res.invitations);
        }
      }),
      catchError(() => {
        this.managers.set([]);
        this.handleCreateManagerError();
        return EMPTY;
      }),
      ignoreElements(),
    );
  }

  public deleteManager(userId: string, isInvitationId: boolean = false) {
    return this.api.deleteManager(userId, isInvitationId).pipe(
      tap(() => {
        // Remove from managers array
        if (!isInvitationId) {
          const managers = this.managers();
          this.managers.set(managers.filter((m) => m.user_id !== userId));
        } else {
          // Remove from invitations array
          const invitations = this.invitations();
          this.invitations.set(invitations.filter((i) => i.id !== userId));
        }
      }),
      catchError((_err: CRError) => {
        const deleteMemberError: CRError = {
          errorCode: PCIAMErrorCodes.DELETE_MEMBER_ERROR,
          severity: 'error',
        };
        return throwError(() => deleteMemberError);
      }),
    );
  }

  public inviteManager(userEmail: string, locale: string) {
    return this.api.inviteManager(userEmail, locale).pipe(
      switchMap((res) => {
        // run createManagers$ to refresh the list but return the original result
        // this is better than using tap
        return this.createManagers$().pipe(ignoreElements(), endWith(res));
      }),
      catchError((_err: CRError) => {
        const inviteError: CRError = {
          errorCode: PCIAMErrorCodes.INVITE_MANAGER_ERROR,
          severity: 'error',
        };
        this.errorHandler.handleError(inviteError);
        return throwError(() => inviteError);
      }),
    );
  }

  private handleCreateOrganizationsError(): void {
    const noOrgsError: CRError = {
      errorCode: PCIAMErrorCodes.GET_ORGANIZATIONS_ERROR,
      severity: 'panic',
    };
    this.errorHandler.handleError(noOrgsError);
  }

  private handleCreateManagerError(): void {
    const createManagersError: CRError = {
      errorCode: PCIAMErrorCodes.GET_ORGANIZATIONS_ERROR,
      severity: 'panic',
    };
    this.errorHandler.handleError(createManagersError);
  }

  public updateProfile(firstName: string, lastName: string) {
    const attributes: IamAttributes = { firstName, lastName };
    return this.api.updateIamAttributes(attributes).pipe(
      catchError(() => {
        const error: CRError = {
          errorCode: PCIAMErrorCodes.UPDATE_ATTRIBUTES_ERROR,
          severity: 'error',
        };
        return throwError(() => error);
      }),
    );
  }

  public updateLanguage(locale: string) {
    const attributes: UserAttributes = { locale: [locale] };
    return this.api.updateIamLanguage(attributes).pipe(
      tap(() => this.translateService.use(locale)),
      catchError(() => {
        const error: CRError = {
          errorCode: PCIAMErrorCodes.UPDATE_LANGUAGE_ERROR,
          severity: 'error',
        };
        return throwError(() => error);
      }),
    );
  }

  public acceptInvitation(organizationId: string, invitationId: string) {
    return this.api.acceptInvitation(organizationId, invitationId).pipe(
      catchError(() => {
        const error: CRError = {
          errorCode: PCIAMErrorCodes.ACCEPT_INVITATION_ERROR,
          severity: 'error',
        };
        return throwError(() => error);
      }),
      // Switch first so the token has an active org. Do not load org/manager lists here —
      // those CR endpoints 401 without an active organization; the initializer loads them after.
      switchMap(() => this.switchOrganization(organizationId)),
    );
  }

  public switchOrganization(targetOrgId: string) {
    // Call the API to update the active organization
    return this.api.updateActiveOrganization(targetOrgId).pipe(
      switchMap(async () => {
        const success = await this.authService.refreshToken(true);

        console.debug('Organization switched, token refresh success:', success);

        if (!success) {
          // In an async switchMap, you can throw an error directly
          throw {
            errorCode: PCIAMErrorCodes.REFRESH_TOKEN_ERROR,
            severity: 'error',
          };
        }
        return success;
      }),
      catchError((error) => {
        if (error.errorCode === PCIAMErrorCodes.REFRESH_TOKEN_ERROR) {
          return throwError(() => error);
        }

        const switchOrgError: CRError = {
          errorCode: PCIAMErrorCodes.SWITCH_ORGANIZATION_ERROR,
          severity: 'error',
        };
        return throwError(() => switchOrgError);
      }),
    );
  }
}
